import React, { useMemo } from "react"
import { Box } from "@mui/material"
import { keyframes } from "@mui/material/styles"

import { dsColorNew } from "@palette"


// One period of the drawing; each curve is drawn twice as wide and slid left by one period, so it loops seamlessly
const PERIOD = 936
const HEIGHT = 900

// Competitor price lines: base height, wave amplitudes, colour, opacity, stroke and seconds per loop
const CURVES = [
	{ y: 300, amp: [38, 18, 9], color: dsColorNew.cyan, opacity: 0.55, width: 2, seconds: 46 },
	{ y: 400, amp: [30, 22, 7], color: dsColorNew.appBlue, opacity: 0.5, width: 1.6, seconds: 60 },
	{ y: 520, amp: [44, 12, 10], color: dsColorNew.white, opacity: 0.22, width: 1.4, seconds: 52 },
	{ y: 610, amp: [26, 20, 6], color: dsColorNew.webBlue500, opacity: 0.6, width: 2, seconds: 70 },
	{ y: 720, amp: [34, 14, 8], color: dsColorNew.cyan, opacity: 0.3, width: 1.2, seconds: 38 },
]

const ACRISS_CODES = ['CDMR', 'EDMR', 'IDAR', 'CFMR', 'ECMR', 'MDMR', 'SDAR', 'PDAR', 'CCMR', 'FFAR', 'IWMR', 'XDAR', 'MBMR', 'LDAR']

const slide = keyframes({
	from: { transform: 'translateX(0)' },
	to: { transform: 'translateX(-50%)' },
})

// Prices move in steps, so the wave is sampled and snapped to a 6px grid
const curvePoints = ({ y, amp }, idx) => {
	const points = []
	for (let x = 0; x <= 2 * PERIOD; x += 8) {
		const t = (x / PERIOD) * 2 * Math.PI
		const value = y + amp[0] * Math.sin(t * 2 + idx) + amp[1] * Math.sin(t * 3 + idx * 2) + amp[2] * Math.sin(t * 7 + idx)
		points.push(`${x},${Math.round(value / 6) * 6}`)
	}
	return points.join(' ')
}


export default function LoginPriceCurves() {
	const curves = useMemo(() => CURVES.map((curve, idx) => ({ ...curve, points: curvePoints(curve, idx) })), [])
	const texture = useMemo(() => Array.from({ length: 900 }, (_, i) => ACRISS_CODES[(i * 5 + Math.floor(i / 14)) % ACRISS_CODES.length]).join(' · '), [])

	return <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
		{/* ACRISS codes as a faint typographic texture */}
		<Box sx={{
			position: 'absolute', inset: -200,
			fontFamily: '"Roboto Mono", monospace', fontSize: 15, lineHeight: '34px', letterSpacing: 6,
			color: dsColorNew.white, opacity: 0.035,
			transform: 'rotate(-12deg)', wordBreak: 'break-all', userSelect: 'none',
		}}>
			{texture}
		</Box>

		{curves.map(({ points, color, opacity, width, seconds }) => <Box
			key={points.slice(0, 24)}
			component="svg"
			viewBox={`0 0 ${2 * PERIOD} ${HEIGHT}`}
			preserveAspectRatio="none"
			sx={{
				position: 'absolute', top: 0, left: 0, width: '200%', height: '100%',
				animation: `${slide} ${seconds}s linear infinite`,
				'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
			}}
		>
			<polyline points={points} fill="none" stroke={color} strokeOpacity={opacity} strokeWidth={width} strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
		</Box>)}
	</Box>
}
